import { authService, storage } from '@innovamat/radiance-utils';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import type {
  LoginHookProps,
  LoginResponse,
  RosterStreamProvider,
} from '../types/login';
import { AuthError } from '../utils/AuthError';

function buildCallbackUrl(provider: RosterStreamProvider): string {
  const origin = `${window.location.origin}${window.location.pathname}`;
  const searchParams = new URLSearchParams(window.location.search);
  const params = new URLSearchParams();

  const dc = searchParams.get('dc');
  const language = searchParams.get('l') || searchParams.get('language');

  if (dc) {
    params.set('dc', dc);
  }
  if (language) {
    params.set('l', language);
  }

  return params.toString() ? `${origin}?${params.toString()}` : origin;
}

export function useRosterStreamLogin({
  dispatch,
  autologin,
  provider,
  rosterStreamAppId,
}: LoginHookProps & { rosterStreamAppId: string }): (
  provider: RosterStreamProvider
) => void {
  const { mutate } = useMutation<LoginResponse, AuthError, string>({
    mutationFn: async (code: string) => {
      return await authService.rosterStreamLogin(code);
    },
  });

  const handleSetLoading = (loading: boolean): void => {
    dispatch({
      type: 'SET_IS_LOADING',
      payload: {
        loading,
        key: { type: 'roster_stream' },
      },
    });
  };

  const handleError = (detail: string): void => {
    const error = new AuthError({
      title: 'roster_stream_error',
      detail,
    });
    dispatch({ type: 'SET_ERROR', payload: error });
    handleSetLoading(false);
  };

  const handleLogin = (provider: RosterStreamProvider): void => {
    const callback = buildCallbackUrl(provider);
    const url = `https://sso.us-east-1.rosterstream.com/login/${provider}/${rosterStreamAppId}?callback=${encodeURIComponent(
      callback
    )}`;
    window.location.href = url;
  };

  const handleCodeLogin = (): void => {
    const searchParams = new URLSearchParams(window.location.search);
    const code = searchParams.get('code');
    if (!code) return;

    searchParams.delete('code');
    searchParams.delete('error');
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}?${searchParams.toString()}`
    );

    handleSetLoading(true);
    mutate(code, {
      onSuccess(tokens) {
        dispatch({
          type: 'SET_LOGIN_INFO',
          payload: {
            response: tokens,
            loggedFrom: { type: 'roster_stream' },
          },
        });
        handleSetLoading(false);
      },
      onError(error) {
        handleError(error.detail || 'RosterStream login failed');
      },
    });
  };

  useEffect(() => {
    if (autologin && provider) {
      handleLogin(provider);
    } else {
      const searchParams = new URLSearchParams(window.location.search);
      if (searchParams.get('code')) {
        if (searchParams.get('dc')) {
          storage.tokenInfo.remove();
        }

        handleCodeLogin();
      }
      if (searchParams.get('error')) {
        handleError(searchParams.get('error') || 'RosterStream login failed');
        searchParams.delete('error');
        searchParams.delete('code');
      }

      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}?${searchParams.toString()}`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autologin, provider]);

  return handleLogin;
}
